import { useCallback, useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import { StateAction, SuggestedWebsite } from '../../types/types';
import Popup from '../Popup/Popup';
import Toggle from '../Toggle/Toggle';
import { AppRoute, ButtonType } from '../../types/enums';
import Button from '../Button/Button';
import { getDisplayUrl } from '../../utils/common';
import { saveSetup } from '../../requests/requests';

import welcome from './welcome.png';
import { ReactComponent as Loading } from './spinner-solid.svg';

import styles from './Onboarding.module.scss';

interface SiteSelectionProps {
  toCheck: SuggestedWebsite[];
  setToCheck: StateAction<SuggestedWebsite[]>;
  next: () => void;
}

const SiteSelection = ({ toCheck, setToCheck, next }: SiteSelectionProps) => {
  const selected = useMemo(() => toCheck.filter((it) => it.active), [toCheck]);

  const toggleSelection = useCallback((url: string) => {
    setToCheck((old: SuggestedWebsite[]) =>
      old.map((it) => {
        if (it.url === url) {
          return { ...it, active: !it.active };
        }

        return { ...it };
      })
    );
  }, []);

  const onSave = useCallback(() => {
    saveSetup(selected);
    setToCheck(selected);
    next();
  }, [selected, next]);

  return (
    <Popup show permanent title="welcome on board">
      <div className={styles.wrapper}>
        <p className={styles.intro}>
          For the best experience, we create your first{' '}
          <strong className="color-orange">Dashboard</strong> and add a few{' '}
          <strong className="color-orange">Websites</strong> to it. Skip this step if you want to
          start from scratch.
        </p>
        <img src={welcome} alt="Setting up" />
        <div className={styles.sites}>
          {toCheck.length === 0 ? (
            <div className={styles.initializing}>
              <Loading />
              <div>
                <p>Initializing...</p>
                <p>This may take a few seconds.</p>
              </div>
            </div>
          ) : (
            toCheck.map((site) => (
              <div key={site.url} className={styles.siteWrapper}>
                <Toggle action={() => toggleSelection(site.url)} active={site.active} />
                <span>{getDisplayUrl(site.url)}</span>
              </div>
            ))
          )}
        </div>
        <div className={styles.buttons}>
          <NavLink to={AppRoute.WEBSITES}>
            <Button spaceSaving type={ButtonType.GHOST}>
              Skip this step
            </Button>
          </NavLink>
          <Button
            disabled={selected.length === 0}
            action={onSave}
            spaceSaving
            type={selected.length === 0 ? ButtonType.PRIMARY_INVERSE : ButtonType.PRIMARY}>
            Add to dashboard
          </Button>
        </div>
      </div>
    </Popup>
  );
};

export default SiteSelection;
